/**
 * ChatPage — Main chat view with modular sidebar integration.
 *
 * Layer: B (Integration)
 * Original: C (UX/CLI) S5–S6 Sidebar + ChatPage rewrite
 *
 * B-INTEGRATION:
 *   - useAuth() for token/user/tenant (not raw token prop)
 *   - useNavigate/useParams for URL-based conversation switching
 *   - useChat hook for streaming (proven, uses lib/sse.ts with VITE_API_URL)
 *   - useConversations adapted to lib/api.ts (auto-refresh, BASE URL)
 *   - ChatLayout from C for responsive sidebar
 *   - ConversationSidebar from C for grouped conversation list
 *   - DeleteDialog from C for confirmation
 *   - ModelSelector from existing components
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useChat, type ChatMessage } from '../hooks/useChat';
import { useConversations } from '../hooks/useConversations';
import { conversations as convApi, type Message } from '../lib/api';
import { Markdown } from '../components/Markdown';
import { ModelSelector } from '../components/ModelSelector';

// ── Layout & Sidebar (C's delivery S5–S6) ────────────────────────────
import { ChatLayout } from '../layouts/ChatLayout';
import { ConversationSidebar } from '../components/sidebar/ConversationSidebar';
import { DeleteDialog } from '../components/sidebar/DeleteDialog';

// ─── Icons ───────────────────────────────────────────────────────────

const IconSend = () => (
  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5" />
  </svg>
);
const IconStop = () => (
  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
    <rect x="6" y="6" width="12" height="12" rx="2" />
  </svg>
);
const IconChat = () => (
  <svg className="h-7 w-7 md:h-8 md:w-8 text-accent/60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.076-4.076a1.526 1.526 0 0 1 1.037-.443 48.282 48.282 0 0 0 5.68-.494c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z" />
  </svg>
);
const IconLogout = () => (
  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9" />
  </svg>
);

// ─── Tool Call Block ──────────────────────────────────────────────────

interface ToolCallData {
  id: string;
  name: string;
  args?: Record<string, unknown>;
  result?: unknown;
  status: 'pending' | 'running' | 'success' | 'error';
}

function ToolCallBlock({ tool }: { tool: ToolCallData }) {
  const [expanded, setExpanded] = useState(false);
  const statusColors: Record<string, string> = {
    pending: 'text-text-secondary', running: 'text-accent animate-pulse',
    success: 'text-success', error: 'text-error',
  };
  return (
    <div className="my-2 rounded-lg border border-border/40 bg-bg-primary/50 text-xs">
      <button onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center gap-2 px-3 py-2.5 text-left hover:bg-bg-tertiary/30 rounded-lg transition-colors min-h-[44px]">
        <span className={`text-[11px] ${statusColors[tool.status]}`}>●</span>
        <span className="font-mono text-text-secondary truncate">{tool.name}</span>
        <span className="ml-auto text-text-secondary/40 text-[10px]">{expanded ? '▾' : '▸'}</span>
      </button>
      {expanded && (
        <div className="border-t border-border/30 px-3 py-2 space-y-2">
          {tool.args && (
            <pre className="overflow-x-auto whitespace-pre-wrap font-mono text-[12px] text-text-secondary/70 break-all">
              {JSON.stringify(tool.args, null, 2)}
            </pre>
          )}
          {tool.result !== undefined && (
            <pre className="overflow-x-auto whitespace-pre-wrap font-mono text-[12px] text-text-secondary/70 break-all">
              {typeof tool.result === 'string' ? tool.result : JSON.stringify(tool.result, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Message Bubble ──────────────────────────────────────────────────

function MessageBubble({ msg }: { msg: ChatMessage }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3 sm:mb-4 md:mb-5`}>
      <div className={`max-w-[92%] sm:max-w-[85%] md:max-w-[80%] lg:max-w-[75%]
        rounded-2xl px-3 py-2.5 sm:px-3.5 sm:py-3 md:px-4 md:py-3
        text-[16px] sm:text-[15px] leading-relaxed ${
        isUser
          ? 'bg-accent/15 text-text-primary rounded-br-md'
          : 'bg-bg-tertiary/60 text-text-primary rounded-bl-md'
      }`}>
        {msg.toolCalls?.map((tool) => <ToolCallBlock key={tool.id} tool={tool} />)}
        {isUser ? (
          <div className="whitespace-pre-wrap break-words">{msg.content}</div>
        ) : (
          <div className="break-words overflow-hidden">
            <Markdown content={msg.content} />
            {msg.streaming && <span className="inline-block h-4 w-0.5 ml-0.5 animate-pulse bg-accent rounded-full" />}
          </div>
        )}
        {msg.usage && (
          <div className="mt-2 flex gap-3 text-[11px] text-text-secondary/50 font-mono">
            <span>{msg.usage.input_tokens + msg.usage.output_tokens} tok</span>
            <span>${msg.usage.cost_usd.toFixed(4)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Chat Input ──────────────────────────────────────────────────────

function ChatInputBar({ onSend, isStreaming, onStop, disabled }: {
  onSend: (content: string) => void; isStreaming: boolean; onStop: () => void; disabled?: boolean;
}) {
  const [input, setInput] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  function handleSubmit() {
    const trimmed = input.trim();
    if (!trimmed || isStreaming || disabled) return;
    onSend(trimmed);
    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(); }
  }

  function handleInput() {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 160) + 'px';
  }

  return (
    <div className="border-t border-border/30 bg-bg-secondary/50 backdrop-blur-sm
      px-2 py-2 sm:px-3 sm:py-3 md:px-4 md:py-4 pb-3 sm:pb-3 md:pb-4">
      <div className="mx-auto flex max-w-3xl items-end gap-2">
        <textarea ref={textareaRef} value={input}
          onChange={(e) => { setInput(e.target.value); handleInput(); }}
          onKeyDown={handleKeyDown}
          placeholder={disabled ? 'Abonnemang krävs...' : 'Skriv ett meddelande...'}
          disabled={disabled} rows={1}
          className="flex-1 resize-none rounded-xl border border-border/50 bg-bg-tertiary/70
            px-3 py-2.5 sm:px-4 sm:py-3 text-[16px] sm:text-[16px] md:text-[15px]
            text-text-primary placeholder-text-secondary/40
            outline-none transition-all duration-200
            focus:border-accent/50 focus:ring-1 focus:ring-accent/20 focus:bg-bg-tertiary
            disabled:opacity-40 disabled:cursor-not-allowed" />
        {isStreaming ? (
          <button onClick={onStop}
            className="flex h-[44px] w-[44px] shrink-0 items-center justify-center
              rounded-xl bg-error/15 text-error transition-all duration-200 hover:bg-error/25
              active:scale-95 touch-manipulation"
            title="Stoppa"><IconStop /></button>
        ) : (
          <button onClick={handleSubmit} disabled={!input.trim() || disabled}
            className="flex h-[44px] w-[44px] shrink-0 items-center justify-center
              rounded-xl bg-accent text-white transition-all duration-200
              hover:bg-accent-hover hover:shadow-lg hover:shadow-accent/20
              active:scale-95 touch-manipulation
              disabled:opacity-20 disabled:cursor-not-allowed"
            title="Skicka"><IconSend /></button>
        )}
      </div>
    </div>
  );
}

// ─── Empty State ─────────────────────────────────────────────────────

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-12 sm:py-16 md:py-20 lg:py-24 text-center px-6">
      <div className="mb-4 md:mb-6 flex h-14 w-14 md:h-16 md:w-16 items-center justify-center rounded-2xl bg-accent/10">
        <IconChat />
      </div>
      <h2 className="font-mono text-base font-medium text-text-primary/80 tracking-wide">AIB-CLAW</h2>
      <p className="mt-2 max-w-xs text-[14px] text-text-secondary/50 leading-relaxed">
        Multi-modell AI-orkestrerare. Skriv ett meddelande för att börja.
      </p>
    </div>
  );
}

// ─── Helper ──────────────────────────────────────────────────────────

function apiMsgToChatMsg(m: Message): ChatMessage {
  return { id: m.id, role: m.role, content: m.content, model: m.model };
}

// ═══════════════════════════════════════════════════════════════════════
// Main ChatPage
// ═══════════════════════════════════════════════════════════════════════

export function ChatPage() {
  const { user, tenant, logout } = useAuth();
  const { id: routeConvId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // ── Hooks ──────────────────────────────────────────────────────────
  const chat = useChat();
  const convos = useConversations();

  const [selectedModel, setSelectedModel] = useState<string | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    title: string | null;
  } | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const prevRouteIdRef = useRef<string | undefined>(undefined);

  const subscriptionStatus = tenant?.subscription_status ?? 'active';
  const isSuspended = subscriptionStatus === 'suspended' || subscriptionStatus === 'canceled';

  // ── Sync route param → conversation ────────────────────────────────

  useEffect(() => {
    if (routeConvId === prevRouteIdRef.current) return;
    prevRouteIdRef.current = routeConvId;

    if (!routeConvId) {
      chat.clearMessages();
      return;
    }

    convos.selectConversation(routeConvId);

    // Load messages for the routed conversation
    let cancelled = false;
    (async () => {
      try {
        const { messages: msgs } = await convApi.get(routeConvId);
        if (cancelled) return;
        chat.loadMessages(msgs.map(apiMsgToChatMsg));
      } catch {
        if (!cancelled) navigate('/chat', { replace: true });
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeConvId]);

  // ── Auto-scroll ────────────────────────────────────────────────────

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chat.messages]);

  // ── Handlers ───────────────────────────────────────────────────────

  const handleSend = useCallback(
    (content: string) => {
      if (routeConvId) {
        chat.sendMessage(content, routeConvId, selectedModel ?? undefined);
      } else {
        // Auto-create conversation, then send
        convos
          .createConversation(selectedModel ?? 'claude-sonnet-4-20250514', content.slice(0, 50))
          .then((newId) => {
            navigate(`/chat/${newId}`, { replace: true });
            chat.sendMessage(content, newId, selectedModel ?? undefined);
          })
          .catch(() => {});
      }
    },
    [routeConvId, chat, convos, selectedModel, navigate],
  );

  const handleNewChat = useCallback(() => {
    chat.clearMessages();
    navigate('/chat', { replace: true });
  }, [chat, navigate]);

  const handleSelectConversation = useCallback(
    (id: string) => {
      navigate(`/chat/${id}`);
    },
    [navigate],
  );

  const handleDeleteRequest = useCallback(
    (id: string) => {
      const conv = convos.conversations.find((c) => c.id === id);
      setDeleteTarget({ id, title: conv?.title ?? null });
    },
    [convos.conversations],
  );

  const handleDeleteConfirm = useCallback(() => {
    if (!deleteTarget) return;
    const deletingActive = deleteTarget.id === routeConvId;
    convos.deleteConversation(deleteTarget.id).catch(() => {});
    setDeleteTarget(null);
    if (deletingActive) {
      handleNewChat();
    }
  }, [deleteTarget, routeConvId, convos, handleNewChat]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteTarget(null);
  }, []);

  // ── Sidebar element ────────────────────────────────────────────────

  const sidebarElement = (
    <ConversationSidebar
      conversations={convos.conversations}
      activeId={routeConvId ?? null}
      isLoading={convos.isLoading}
      onSelect={handleSelectConversation}
      onDelete={handleDeleteRequest}
      onNewChat={handleNewChat}
    />
  );

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <>
      <ChatLayout sidebar={sidebarElement}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <header className="flex items-center justify-between border-b border-border/30 bg-bg-secondary/60 backdrop-blur-sm
            px-2 py-2 sm:px-3 sm:py-2.5 md:px-4 md:py-3 lg:px-5">
            <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3">
              <span className="font-mono text-[14px] font-semibold tracking-widest text-accent/80 hidden md:block">CLAW</span>
              <span className="text-border hidden md:block">|</span>
              <ModelSelector
                value={selectedModel}
                onChange={(model, provider) => {
                  setSelectedModel(model);
                  setSelectedProvider(provider);
                }}
              />
            </div>
            <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
              {user && (
                <span className="hidden md:block text-[13px] text-text-secondary/60 truncate max-w-[200px]">
                  {user.email}
                </span>
              )}
              {subscriptionStatus !== 'active' && (
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-mono font-medium ${
                  subscriptionStatus === 'trialing' ? 'bg-accent/10 text-accent'
                  : subscriptionStatus === 'past_due' ? 'bg-warning/10 text-warning'
                  : 'bg-error/10 text-error'
                }`}>{subscriptionStatus}</span>
              )}
              <button onClick={logout}
                className="flex h-[40px] w-[40px] md:w-auto md:h-auto items-center justify-center rounded-lg
                  text-text-secondary/50 transition-colors hover:text-text-primary hover:bg-bg-tertiary/50
                  md:hover:bg-transparent md:rounded-none md:px-0 touch-manipulation"
                title="Logga ut">
                <span className="md:hidden"><IconLogout /></span>
                <span className="hidden md:block text-[13px]">Logga ut</span>
              </button>
            </div>
          </header>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto overscroll-contain
            px-2 py-3 sm:px-3 sm:py-4 md:px-4 md:py-5 lg:px-6 lg:py-6 scrollbar-thin">
            <div className="mx-auto max-w-3xl">
              {chat.messages.length === 0 && <EmptyState />}
              {chat.messages.map((msg) => (
                <MessageBubble key={msg.id} msg={msg} />
              ))}
              {chat.error && (
                <div className="mb-4 rounded-xl bg-error/10 border border-error/20 px-3 py-2.5 md:px-4 md:py-3 text-[14px] text-error">
                  {chat.error}
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input */}
          <ChatInputBar
            onSend={handleSend}
            isStreaming={chat.isStreaming}
            onStop={chat.stopStreaming}
            disabled={isSuspended}
          />
        </div>
      </ChatLayout>

      {/* Delete confirmation dialog */}
      <DeleteDialog
        isOpen={deleteTarget !== null}
        conversationTitle={deleteTarget?.title ?? null}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </>
  );
}
